// fakeData.js
// Simulated sensor data for the two non-privileged auth tiers.
//
// Guests and signed-in users who are NOT in the Cognito 'superusers' group must
// never see real measurements, and — just as important — must never cause a
// request to /api/. Every generator here returns the exact shape the matching
// endpoint would return, so callers can swap a fetch for a generator call
// without touching their downstream transform.
//
// The pin / latest-reading / anomaly generators are intentionally re-rolled on
// every call (fresh Math.random). The chart series generators are seeded from
// the deviceId instead, so a given sensor keeps a stable-looking history across
// re-renders, range switches and the 5-minute poll — a chart whose whole shape
// jumped every five minutes would read as broken rather than as demo data.

// Emits pins in the end-node shape the map expects: one pin is one node, and a
// node carries its sensors in a `sensors` array. `status` stays on the node
// because generated pins have no /api/health record to read a status from.
export function generateFakePins(count = 3) {
  const types = ['tilt', 'pir', 'temperature'];
  const statuses = ['ok', 'ok', 'ok', 'ok', 'ok', 'ok', 'ok', 'warning', 'warning', 'alert'];
  return Array.from({ length: count }, (_, i) => {
    const id = i + 1;
    return {
      id,
      nodeId: `fake-node-${id}`,
      label: `Node ${id}`,
      position: [
        42.38 + Math.random() * 0.12,
        19.15 + Math.random() * 0.17,
      ],
      status: statuses[Math.floor(Math.random() * statuses.length)],
      sensors: [
        {
          deviceId: 'sensor-' + Math.random().toString(36).slice(2, 6),
          sensor_type: types[Math.floor(Math.random() * types.length)],
        },
      ],
    };
  });
}

// Health records keyed by device id, in the shape /api/health returns.
//
// The Sidebar builds its sensor list from `Object.values(healthMap)` and shows
// "Loading…" whenever that is empty and `healthError` is false. Health polling
// is superuser-only, so for every other tier the map stayed empty and the
// sidebar showed a permanent "Loading…" that would never resolve. Synthesising
// records from the generated nodes keeps the same promise as the rest of this
// file: the tier sees plausible data, and no request reaches /api/.
export function generateFakeHealthMap(pins = []) {
  const map = {};
  pins.forEach(pin => {
    (pin.sensors ?? []).forEach(sensor => {
      const status = pin.status ?? 'ok';
      // One elapsed value drives every time field, so the age on screen and the
      // exact timestamp in its tooltip agree. They did not before: last_seen
      // was always "now" while hours_since_seen was up to 1.5 hours. The 0-90
      // minute spread keeps the original range and exercises all three of
      // formatLastSeen's branches — just now, minutes, hours.
      const secondsSinceSeen = Math.floor(Math.random() * 5400);
      map[sensor.deviceId] = {
        device_id: sensor.deviceId,
        label: pin.label ?? sensor.deviceId,
        status,
        // A warning tier should look like the reason it is warning about.
        battery: status === 'warning'
          ? Math.floor(5 + Math.random() * 14)
          : Math.floor(40 + Math.random() * 60),
        seconds_since_seen: secondsSinceSeen,
        hours_since_seen: parseFloat((secondsSinceSeen / 3600).toFixed(1)),
        last_seen: new Date(Date.now() - secondsSinceSeen * 1000).toISOString(),
      };
    });
  });
  return map;
}

export function generateFakeSensorData(deviceId, sensorType) {
  const base = { PK: deviceId, SK: new Date().toISOString(), sensor_type: sensorType };
  if (sensorType === 'tilt') return {
    ...base,
    payload: {
      angle_x: (Math.random() * 10 - 5).toFixed(2),
      angle_y: (Math.random() * 10 - 5).toFixed(2),
      angle_z: (Math.random() * 10 - 5).toFixed(2),
      threshold_x: 'normal', threshold_y: 'normal', threshold_z: 'normal',
    }
  };
  if (sensorType === 'temperature') return {
    ...base,
    payload: { temperature: (18 + Math.random() * 17).toFixed(1) }
  };
  // Electrical meter (#99). Field names are the normalised SAMST ones, so the
  // raw-reading box shows a non-superuser the same shape a superuser sees. Only
  // the one wired phase, matching the pilot meter — filling in phase B and C with
  // zeros is exactly the fabricated-zero the real decoder refuses to produce.
  if (sensorType === 'power') return {
    ...base,
    payload: {
      voltage_a_v: (228 + Math.random() * 10).toFixed(1),
      current_a_a: (0.4 + Math.random() * 3.4).toFixed(2),
      active_power_kw: (Math.random() * 0.9 - 0.1).toFixed(3),
      power_factor: (0.86 + Math.random() * 0.139).toFixed(3),
      energy_total_kwh: (Math.random() * 80).toFixed(2),
    }
  };
  return { ...base, payload: { pir: 'normal' } };
}

// Shape of GET /api/anomalies (since #54: sourced from SamstAnomalies).
// Scenarios are fixed rather than randomly recombined so that type, severity,
// description and payload stay consistent with each other — a 'pir' row saying
// "X axis drift" was possible before and looked like a bug. Ordering matters:
// the list is cycled in order, so the first few entries are what the default
// FAKE_ANOMALY_COUNT shows.
//
// The mix is deliberate. At least two rows are above 'low' so the panel is not
// empty with the default motion filter on; at least one is 'low' so toggling
// "show motion events" visibly changes something; and one carries
// weather_correlated so the 🌩 tag is exercised without a live weather feed.
const FAKE_ANOMALY_SCENARIOS = [
  {
    sensor_type: 'tilt',
    severity: 'high',
    confidence: '0.80',
    description: 'Y axis hardware threshold triggered; Z axis drift -10.8°',
    raw_payload: {
      angle_x: '1.02', angle_y: '-79.15', angle_z: '-10.79',
      threshold_x: 'normal', threshold_y: 'trigger', threshold_z: 'normal',
      battery: '100',
    },
    weather_correlated: false,
    // A sensor that has worked loose crosses its own threshold all evening, and
    // every crossing is a true anomaly. The API folds such a run into one entry
    // (#211) and the panel shows it as a span with a count (#208); without one
    // here, no tier but a logged-in super user ever sees that half of the panel.
    rollup: { count: 192, spanMs: 3 * 3600000 },
  },
  {
    sensor_type: 'temperature',
    severity: 'critical',
    confidence: '0.93',
    description: 'Temperature out of range: 62.4°C (too high)',
    raw_payload: { temperature: '62.4', battery: '88' },
    weather_correlated: true,
    weather_summary: 'RED alert: Extreme high temperature — Wind gusts 71 km/h',
  },
  {
    sensor_type: 'pir',
    severity: 'low',
    confidence: '0.90',
    description: 'Motion detected by PIR sensor',
    raw_payload: { pir: 'trigger', light: 'light', battery: '45' },
    weather_correlated: false,
  },
  {
    sensor_type: 'tilt',
    severity: 'critical',
    confidence: '1.00',
    description: 'X axis hardware threshold triggered; Y axis hardware threshold triggered',
    raw_payload: {
      angle_x: '24.60', angle_y: '31.20', angle_z: '2.10',
      threshold_x: 'trigger', threshold_y: 'trigger', threshold_z: 'normal',
      battery: '76',
    },
    weather_correlated: true,
    weather_summary: 'ORANGE alert: Wind — Wind gusts 84 km/h',
  },
  {
    sensor_type: 'temperature',
    severity: 'medium',
    confidence: '0.28',
    description: 'Temperature out of range: -1.1°C (too low)',
    raw_payload: { temperature: '-1.1', battery: '91' },
    weather_correlated: false,
  },
  {
    sensor_type: 'pir',
    severity: 'low',
    confidence: '0.90',
    description: 'Motion detected by PIR sensor',
    raw_payload: { pir: 'trigger', light: 'dark', battery: '44' },
    weather_correlated: false,
  },
];

const FAKE_ANOMALY_DEVICES = [
  'tilt-sensor-em320-2',
  'ts201-temp-sens-1',
  'pir-light-ws202',
  'tilt-sensor-em320',
  'ts201-temp-sens-1',
  'pir-light-ws202',
];

// Which tower each generated device is bolted to — the join the API makes from
// SamstNodes for a real deployment (#208). Without it every generated row heads
// its own group under a device id, and the tower grouping the panel is built
// around is invisible to every tier that does not call /api/.
//
// Node 1 deliberately carries two devices, which is what makes the panel show
// the device column on a row; Node 2 and Node 4 carry one each.
const FAKE_ANOMALY_NODES = {
  'tilt-sensor-em320-2': { node_id: 'fake-node-2', node_label: 'Node 2' },
  'ts201-temp-sens-1':   { node_id: 'fake-node-1', node_label: 'Node 1' },
  'pir-light-ws202':     { node_id: 'fake-node-1', node_label: 'Node 1' },
  'tilt-sensor-em320':   { node_id: 'fake-node-4', node_label: 'Node 4' },
};

// The rows behind a folded entry's count, newest first and evenly spread across
// its span. Capped the same way the API caps its sample, so the panel's "newest
// 20 of 192" line is exercised rather than only the complete case.
const FAKE_MEMBER_SAMPLE_MAX = 20;
const FAKE_MEMBER_WORDINGS = [
  'Y axis hardware threshold triggered; Z axis drift -10.8°',
  'X axis hardware threshold triggered',
  'Y axis hardware threshold triggered',
  'X axis hardware threshold triggered; Y axis hardware threshold triggered',
];

function fakeMembers(endMs, rollup) {
  const shown = Math.min(rollup.count, FAKE_MEMBER_SAMPLE_MAX);
  const step = rollup.spanMs / Math.max(rollup.count - 1, 1);
  return Array.from({ length: shown }, (_, i) => ({
    timestamp: new Date(endMs - i * step).toISOString(),
    description: FAKE_MEMBER_WORDINGS[i % FAKE_MEMBER_WORDINGS.length],
  }));
}

export function generateFakeAnomalies(count = 3) {
  return Array.from({ length: count }, (_, i) => {
    const s = FAKE_ANOMALY_SCENARIOS[i % FAKE_ANOMALY_SCENARIOS.length];
    const device_id = FAKE_ANOMALY_DEVICES[i % FAKE_ANOMALY_DEVICES.length];
    const node = FAKE_ANOMALY_NODES[device_id] ?? { node_id: null, node_label: null };
    const endMs = Date.now() - i * 3600000 * 4;
    const rollup = s.rollup ?? null;
    return {
      // `id` is not part of the issue spec but AnomaliesPanel keys its cards and
      // its per-card expansion state off it; without one every fake card shares a
      // key and expanding one expands all of them.
      id: 'fake-anomaly-' + i,
      device_id,
      node_id: node.node_id,
      node_label: node.node_label,
      timestamp: new Date(endMs).toISOString(),
      // The oldest row of the fold, which is the left half of the span the
      // panel prints. Equal to `timestamp` on an entry that was not folded, the
      // same shape the API gives an un-rolled row.
      first_timestamp: new Date(endMs - (rollup?.spanMs ?? 0)).toISOString(),
      sensor_type: s.sensor_type,
      severity: s.severity,
      confidence: s.confidence,
      description: s.description,
      weather_correlated: s.weather_correlated,
      weather_summary: s.weather_summary ?? null,
      raw_payload: s.raw_payload,
      count: rollup?.count ?? 1,
      rolled_up: Boolean(rollup),
      truncated: false,
      variant_count: rollup ? FAKE_MEMBER_WORDINGS.length : 1,
      members: rollup ? fakeMembers(endMs, rollup) : [],
    };
  });
}

// ── chart series ────────────────────────────────────────────────────────────

// Mulberry32 — small deterministic PRNG so a deviceId always yields the same
// series. Returns floats in [0, 1) like Math.random.
function seededRandom(seed) {
  let a = seed >>> 0;
  return function next() {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function hashString(str) {
  let h = 2166136261;
  for (let i = 0; i < String(str).length; i++) {
    h ^= String(str).charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

// How many points to draw, and how far apart, for each range the charts offer.
const RANGE_SPEC = {
  '24h': { points: 24, stepMs: 3600000 },
  '7d': { points: 84, stepMs: 2 * 3600000 },
  '30d': { points: 120, stepMs: 6 * 3600000 },
};

// Evenly spaced ISO timestamps ending "now", oldest first.
function timeAxis(range, points) {
  const spec = RANGE_SPEC[range] ?? RANGE_SPEC['7d'];
  const n = points ?? spec.points;
  const end = Date.now();
  return Array.from({ length: n }, (_, i) =>
    new Date(end - (n - 1 - i) * spec.stepMs).toISOString()
  );
}

// Shape of GET /api/drift?device_id&range.
export function generateFakeDriftSeries(deviceId, range = '7d') {
  const rand = seededRandom(hashString(deviceId) ^ hashString(range));
  const stamps = timeAxis(range);
  const driftThreshold = 1.0;

  const baseline = {
    angle_x: rand() * 6 - 3,
    angle_y: rand() * 6 - 3,
    angle_z: rand() * 6 - 3,
    is_partial: false,
    n_samples: 240,
  };

  // Slow wander plus per-point jitter, so the moving average the chart overlays
  // has something to smooth.
  let wx = 0, wy = 0, wz = 0;
  let alertCount = 0;
  const data = stamps.map((timestamp) => {
    wx += (rand() - 0.5) * 0.09;
    wy += (rand() - 0.5) * 0.09;
    wz += (rand() - 0.5) * 0.05;
    const drift_x = parseFloat((wx + (rand() - 0.5) * 0.06).toFixed(4));
    const drift_y = parseFloat((wy + (rand() - 0.5) * 0.06).toFixed(4));
    const drift_z = parseFloat((wz + (rand() - 0.5) * 0.04).toFixed(4));
    const drift_magnitude = parseFloat(
      Math.sqrt(drift_x ** 2 + drift_y ** 2 + drift_z ** 2).toFixed(4)
    );
    const has_alert = drift_magnitude > driftThreshold;
    if (has_alert) alertCount++;
    return { timestamp, drift_x, drift_y, drift_z, drift_magnitude, has_alert };
  });

  return {
    device_id: deviceId,
    baseline,
    alert_count: alertCount,
    drift_threshold: driftThreshold,
    data,
  };
}

// Shape of GET /api/drift/trend?device_id (#130). Every tier below superuser is
// simulated, so without this the degradation panel would either be blank for
// most users or would fetch real site measurements for them.
//
// The generated velocities are deliberately small and cross zero, because that
// is what the real pilot towers do: measured velocity on all three sits inside
// ±0.06 °/day and changes sign between windows. Generating a dramatic
// degradation would teach a demo user that the panel normally shows one.
export function generateFakeDriftTrend(deviceId, windowDays = 30) {
  const rand = seededRandom(hashString(deviceId) ^ hashString('trend'));
  const now = Date.now();
  const iso = (msAgo) => new Date(now - msAgo).toISOString();
  const windowMs = windowDays * 86400000;
  const round = (v) => parseFloat(v.toFixed(6));

  const velocity = round((rand() - 0.5) * 0.05);
  const previous = round((rand() - 0.5) * 0.05);
  const coefficient = round((rand() - 0.4) * 0.1);

  // Six earlier windows, oldest last, so history.windows[0] is the previous one.
  const windows = Array.from({ length: 6 }, (_, i) => ({
    start: iso((i + 2) * windowMs),
    end: iso((i + 1) * windowMs),
    velocity_deg_per_day: i === 0 ? previous : round((rand() - 0.5) * 0.05),
    n_readings: 1500 + Math.floor(rand() * 8000),
    coverage: parseFloat((0.6 + rand() * 0.4).toFixed(4)),
    insufficient: null,
  }));
  const fitted = windows.map((w) => w.velocity_deg_per_day);
  const sorted = [...fitted].sort((a, b) => a - b);
  const med = sorted[Math.floor(sorted.length / 2)];
  const sigma = round(
    1.4826 * [...fitted.map((v) => Math.abs(v - med))].sort((a, b) => a - b)[
      Math.floor(fitted.length / 2)
    ]
  );

  const acceleration = round(velocity - previous);
  const threshold = round(med + 3 * sigma);
  const nReadings = 1500 + Math.floor(rand() * 8000);

  return {
    device_id: deviceId,
    window_days: windowDays,
    window_start: iso(windowMs),
    window_end: iso(0),
    velocity_deg_per_day: velocity,
    velocity_insufficient: null,
    previous_window_start: iso(2 * windowMs),
    previous_window_end: iso(windowMs),
    previous_velocity_deg_per_day: previous,
    acceleration_deg_per_day_per_window: acceleration,
    axis_velocity_deg_per_day: {
      drift_x: round(velocity * 0.4),
      drift_y: round(velocity * 0.9),
      drift_z: round(-velocity * 0.3),
    },
    n_readings: nReadings,
    n_readings_total: nReadings * 7,
    n_excluded_no_angle_data: Math.floor(rand() * 30),
    n_excluded_total: Math.floor(rand() * 30),
    fit: {
      estimator: 'theil-sen',
      binning: 'median per equal-duration bin',
      n_bins: 60,
      n_bins_filled: 50 + Math.floor(rand() * 10),
      bin_span_ms: windowMs / 60,
      n_pairs: 1540,
      coverage: parseFloat((0.85 + rand() * 0.15).toFixed(4)),
    },
    temperature: {
      adjusted: true,
      reason: null,
      n_bins_paired: 50 + Math.floor(rand() * 10),
      coefficient_deg_per_c: coefficient,
      correlation: round((rand() - 0.5) * 1.2),
      mean_c: parseFloat((18 + rand() * 10).toFixed(2)),
      min_c: parseFloat((10 + rand() * 5).toFixed(2)),
      max_c: parseFloat((30 + rand() * 8).toFixed(2)),
      velocity_adjusted: round(velocity - coefficient * 0.2),
      velocity_adjusted_insufficient: null,
    },
    // Provenance of that temperature (#158). The real endpoint reads weather
    // over the whole 210-day lookback, not just the reported window, and on the
    // real table that span starts well before the 2026-07-28 splice between the
    // ERA5 backfill and the live Open-Meteo collector — so the real panel shows
    // the boundary warning. A simulated panel that quietly showed a single-source
    // window would be a rosier picture than the deployment actually has, which is
    // the one thing fake data must not be.
    temperature_source: {
      table: 'SamstWeather',
      rows_available: 6000 + Math.floor(rand() * 3000),
      table_missing: false,
      window_summary: {
        window: {
          from: iso(7 * windowMs),
          to: iso(0),
          days: parseFloat((7 * windowDays).toFixed(2)),
        },
        n_rows: 6000 + Math.floor(rand() * 3000),
        rows_per_day: 42.6,
        aggregation: 'mean of fixed-interval means',
        resample: {
          interval_ms: 3600000,
          n_intervals: 7 * windowDays * 24,
          n_intervals_filled: 7 * windowDays * 24,
          coverage: 1,
        },
        mean_c: parseFloat((18 + rand() * 8).toFixed(2)),
        mean_c_row_weighted: parseFloat((20 + rand() * 8).toFixed(2)),
        cadence_bias_c: parseFloat((rand() * 3).toFixed(2)),
        min_c: parseFloat((-2 + rand() * 6).toFixed(2)),
        max_c: parseFloat((34 + rand() * 6).toFixed(2)),
        sources: { 'era5-archive': 3400, 'open-meteo-forecast': 4300 },
        n_unlabelled_rows: 0,
        crosses_source_boundary: true,
        source_join_at: '2026-07-28T13:18:52.729968+00:00',
        window_spans_source_join: true,
      },
    },
    history: {
      window_days: windowDays,
      n_windows_requested: 6,
      n_windows_fitted: 6,
      median_velocity_deg_per_day: round(med),
      sigma_deg_per_day: sigma,
      windows,
    },
    flag: {
      raised: velocity > 0 && acceleration > 0 && velocity > threshold,
      calibrated: false,
      sigma_k: 3,
      threshold_deg_per_day: threshold,
      reason: null,
      criteria: {
        velocity_positive: velocity > 0,
        velocity_increasing: acceleration > 0,
        exceeds_own_history: velocity > threshold,
      },
    },
    computed_at: iso(0),
    cached: false,
    age_ms: 0,
  };
}

// Shape of GET /api/sensors/:deviceId?range — history of temperature records.
export function generateFakeTemperatureSeries(deviceId, range = '7d') {
  const rand = seededRandom(hashString(deviceId) ^ hashString('temp' + range));
  const stamps = timeAxis(range);
  const mean = 16 + rand() * 8;
  return stamps.map((SK, i) => {
    // Diurnal swing plus noise.
    const diurnal = Math.sin((i / 12) * Math.PI) * 4.5;
    return {
      PK: deviceId,
      SK,
      sensor_type: 'temperature',
      payload: { temperature: (mean + diurnal + (rand() - 0.5) * 1.8).toFixed(1) },
    };
  });
}

// Shape of GET /api/station?device_id — current readings for an S2120 weather
// station (#77). Every tier below superuser is simulated, so without this the
// panel would either be blank for most users or would fetch real site
// measurements for them.
//
// Each reading carries its own measured_at because the real station splits
// measurements and status across separate uplinks — battery is deliberately
// generated hours old, which is what it genuinely looks like in production.
export function generateFakeStationReadings(deviceId) {
  const rand = seededRandom(hashString(deviceId) ^ hashString('station'));
  const now = Date.now();
  const at = (minutesAgo) => new Date(now - minutesAgo * 60000).toISOString();
  const recent = at(2 + Math.floor(rand() * 6));

  const reading = (value, minutesAgo) => ({ value, measured_at: minutesAgo ? at(minutesAgo) : recent });

  return {
    device_id: deviceId,
    last_seen: recent,
    readings: {
      air_temperature_c:  reading(parseFloat((14 + rand() * 14).toFixed(1))),
      humidity_pct:       reading(Math.round(45 + rand() * 35)),
      pressure_hpa:       reading(parseFloat((1002 + rand() * 18).toFixed(1))),
      wind_speed_ms:      reading(parseFloat((rand() * 9).toFixed(1))),
      wind_direction_deg: reading(Math.round(rand() * 359)),
      illuminance_lux:    reading(Math.round(rand() * 60000)),
      uv_index:           reading(Math.round(rand() * 7)),
      wind_gust_ms:       reading(parseFloat((9 + rand() * 6).toFixed(1))),
      // Mostly dry, like the real site. Generated as rate and 24 h total so the
      // panel's rain figures appear for every tier, not only on real data.
      rain_rate_mmh:      reading(rand() < 0.6 ? 0 : parseFloat((rand() * 6).toFixed(1))),
      rain_24h_mm:        { ...reading(parseFloat((rand() * 14).toFixed(1))), unknown_intervals: 0, complete: true },
      battery:            reading(Math.round(35 + rand() * 65), 60 + Math.floor(rand() * 600)),
      firmware_version:   reading('2.0', 240),
      hardware_version:   reading('1.1', 240),
    },
    unverified: {},
    // Empty since #243, as the real API returns it: no channel is withheld.
    unverified_fields: [],
  };
}

// Shape of GET /api/station/series?device_id&range — weather station history
// (#85). Seeded from deviceId + range like the other chart generators, so the
// curves stay stable across re-renders and the five-minute poll.
//
// Each parameter is given a plausible shape rather than noise, because the point
// of the chart is to read shape: temperature and illuminance follow a diurnal
// cycle, pressure drifts slowly, humidity runs inverse to temperature, and wind
// direction wanders and wraps through north — which is what exercises the
// circular handling in the chart.
export function generateFakeStationSeries(deviceId, range = '7d') {
  const rand = seededRandom(hashString(deviceId) ^ hashString('station' + range));
  const stamps = timeAxis(range);

  const tempMean = 16 + rand() * 8;
  let pressure = 1004 + rand() * 12;
  let bearing = rand() * 360;
  // Rain arrives in showers: a few runs of wet intervals in an otherwise dry
  // series, so the bars look like weather and most of the axis reads zero.
  let showerLeft = 0;

  const data = stamps.map((ts) => {
    const hour = new Date(ts).getHours();
    // Peak around 15:00, trough around 03:00.
    const diurnal = Math.sin(((hour - 9) / 24) * 2 * Math.PI);
    // Daylight window only, so illuminance is genuinely 0 overnight.
    const daylight = Math.max(0, Math.sin(((hour - 6) / 12) * Math.PI));

    pressure += (rand() - 0.5) * 0.6;
    // A free wander with wraparound rather than a bounded 0-360 value, so the
    // chart's circular handling is exercised in simulated mode. It crosses north
    // in roughly half of deviceId/range seeds — often enough to notice a
    // regression, without forcing an unrealistic full rotation every time.
    bearing = (bearing + (rand() - 0.5) * 50 + 360) % 360;

    const temp = tempMean + diurnal * 5 + (rand() - 0.5) * 0.8;
    if (showerLeft === 0 && rand() < 0.03) showerLeft = 2 + Math.floor(rand() * 8);
    const raining = showerLeft > 0;
    if (raining) showerLeft--;
    const wind = Math.max(0, daylight * 4 + (rand() - 0.5) * 2);
    return {
      ts,
      air_temperature_c: parseFloat(temp.toFixed(1)),
      humidity_pct: Math.round(Math.min(95, Math.max(30, 70 - diurnal * 15 + (rand() - 0.5) * 6))),
      pressure_hpa: parseFloat(pressure.toFixed(1)),
      wind_speed_ms: parseFloat(wind.toFixed(1)),
      // A gust is never below the wind speed of the same reading.
      wind_gust_ms: parseFloat((wind + 1 + rand() * 4).toFixed(1)),
      // Whole tips of a 0.254 mm bucket, as the real gauge reports.
      rain_mm: raining ? parseFloat((0.254 * (1 + Math.floor(rand() * 6))).toFixed(3)) : 0,
      wind_direction_deg: Math.round(bearing),
      illuminance_lux: Math.round(daylight * 55000 * (0.7 + rand() * 0.3)),
      uv_index: Math.round(daylight * 7 * (0.7 + rand() * 0.3)),
    };
  });

  const fields = [
    'air_temperature_c', 'humidity_pct', 'illuminance_lux',
    'pressure_hpa', 'wind_direction_deg', 'wind_speed_ms', 'uv_index',
    'wind_gust_ms', 'rain_mm',
  ];

  return {
    device_id: deviceId,
    range,
    fields,
    available_fields: fields,
    circular_fields: ['wind_direction_deg'],
    data,
    total_points: data.length,
    raw_readings: data.length,
    measurement_rows: data.length,
    bucket_size: 1,
  };
}

// Shape of GET /api/meter?device_id — Acrel ADW300 current readings (#99).
//
// Generated as one wired phase, matching the pilot meter, so the simulated panel
// shows the same single-phase layout a superuser sees rather than implying a
// three-phase supply the site does not have. Phase B and C are absent, not zero —
// the real API withholds them (see DataIngestionESM/lib/adw300.mjs), and a
// generator that filled them with 0 would teach the panel a habit the real data
// punishes.
export function generateFakeMeterReadings(deviceId) {
  const rand = seededRandom(hashString(deviceId) ^ hashString('meter'));
  const now = Date.now();
  const at = (minutesAgo) => new Date(now - minutesAgo * 60000).toISOString();
  const recent = at(1 + Math.floor(rand() * 3));
  const reading = (value) => ({ value, measured_at: recent });

  const voltage = parseFloat((228 + rand() * 10).toFixed(1));
  const current = parseFloat((0.4 + rand() * 3.4).toFixed(2));
  const pf = parseFloat((0.86 + rand() * 0.139).toFixed(3));
  // Apparent power from S = U*I*(distortion factor). The pilot meter's S sits
  // below U*I at light load, so the generated figures keep that relationship
  // rather than presenting a textbook one the real device does not produce.
  const apparent = parseFloat(((voltage * current * (0.72 + pf * 0.28)) / 1000).toFixed(3));
  const active = parseFloat((apparent * pf).toFixed(3));
  const reactive = parseFloat((apparent * Math.sqrt(Math.max(0, 1 - pf * pf))).toFixed(3));
  const forward = parseFloat((rand() * 40).toFixed(2));
  const reverse = parseFloat((rand() * 40).toFixed(2));

  return {
    device_id: deviceId,
    last_seen: recent,
    phases_present: ['a'],
    readings: {
      voltage_a_v: reading(voltage),
      current_a_a: reading(current),
      active_power_kw: reading(active),
      reactive_power_kvar: reading(reactive),
      apparent_power_kva: reading(apparent),
      power_factor: reading(pf),
      energy_total_kwh: reading(parseFloat((forward + reverse).toFixed(2))),
      energy_forward_kwh: reading(forward),
      energy_reverse_kwh: reading(reverse),
      reactive_energy_forward_kvarh: reading(parseFloat((rand() * 12).toFixed(2))),
      reactive_energy_reverse_kvarh: reading(parseFloat((rand() * 4).toFixed(2))),
      active_power_a_kw: reading(active),
      reactive_power_a_kvar: reading(reactive),
      apparent_power_a_kva: reading(apparent),
      power_factor_a: reading(pf),
      pt_ratio: reading(1),
      ct_ratio: reading(1),
    },
    unverified: {},
    // Named so the panel's "not shown as measurements" note appears in simulated
    // mode too, rather than being a surprise only on real data.
    unverified_fields: ['temp_n_c', 'di_status', 'unknown_45 to unknown_68'],
  };
}

// Shape of GET /api/meter/series?device_id&range — ADW300 history (#99).
//
// The instantaneous channels follow a working-hours load curve so the chart has
// shape to read, and the cumulative registers only ever rise — a generated energy
// register that wobbled would hide a real regression in the chart's handling of
// cumulative fields, which are carried as the bucket's last value rather than its
// mean.
export function generateFakeMeterSeries(deviceId, range = '7d') {
  const rand = seededRandom(hashString(deviceId) ^ hashString('meter' + range));
  const stamps = timeAxis(range);

  let forward = 20 + rand() * 30;
  let reverse = 10 + rand() * 20;
  let reactiveFwd = 4 + rand() * 6;
  let reactiveRev = 1 + rand() * 3;

  const data = stamps.map((ts) => {
    const hour = new Date(ts).getHours();
    // Load rises through the working day and falls back to a small standing
    // draw overnight.
    const duty = Math.max(0.12, Math.sin(Math.max(0, (hour - 6) / 14) * Math.PI));

    const voltage = parseFloat((231 + Math.sin(hour / 3) * 2.5 + (rand() - 0.5) * 1.4).toFixed(1));
    const current = parseFloat(Math.max(0.4, duty * 3.6 + (rand() - 0.5) * 0.3).toFixed(2));
    const pf = parseFloat(Math.min(0.999, 0.86 + duty * 0.13 + (rand() - 0.5) * 0.02).toFixed(3));
    const apparent = parseFloat(((voltage * current * (0.72 + pf * 0.28)) / 1000).toFixed(3));
    const active = parseFloat((apparent * pf).toFixed(3));
    const reactive = parseFloat((apparent * Math.sqrt(Math.max(0, 1 - pf * pf))).toFixed(3));

    forward += active * 0.25;
    reverse += rand() * 0.02;
    reactiveFwd += reactive * 0.2;
    reactiveRev += rand() * 0.004;

    return {
      ts,
      voltage_a_v: voltage,
      current_a_a: current,
      active_power_kw: active,
      reactive_power_kvar: reactive,
      apparent_power_kva: apparent,
      power_factor: pf,
      energy_total_kwh: parseFloat((forward + reverse).toFixed(2)),
      energy_forward_kwh: parseFloat(forward.toFixed(2)),
      energy_reverse_kwh: parseFloat(reverse.toFixed(2)),
      reactive_energy_forward_kvarh: parseFloat(reactiveFwd.toFixed(2)),
      reactive_energy_reverse_kvarh: parseFloat(reactiveRev.toFixed(2)),
      active_power_a_kw: active,
      reactive_power_a_kvar: reactive,
      apparent_power_a_kva: apparent,
      power_factor_a: pf,
    };
  });

  // Only the one wired phase, so the chart offers the same parameters it would
  // for the real meter.
  const fields = [
    'voltage_a_v', 'current_a_a', 'active_power_kw', 'reactive_power_kvar',
    'apparent_power_kva', 'power_factor', 'energy_total_kwh',
    'energy_forward_kwh', 'energy_reverse_kwh',
    'reactive_energy_forward_kvarh', 'reactive_energy_reverse_kvarh',
    'active_power_a_kw', 'reactive_power_a_kvar', 'apparent_power_a_kva',
    'power_factor_a',
  ];

  return {
    device_id: deviceId,
    range,
    fields,
    available_fields: fields,
    cumulative_fields: [
      'energy_total_kwh', 'energy_forward_kwh', 'energy_reverse_kwh',
      'reactive_energy_forward_kvarh', 'reactive_energy_reverse_kvarh',
    ],
    data,
    total_points: data.length,
    raw_readings: data.length,
    measurement_rows: data.length,
    bucket_size: 1,
  };
}

// Shape of GET /api/sensors/:deviceId?range — history of PIR records. Only the
// 'trigger' rows become events in the log; 'normal' rows exercise the filter.
export function generateFakePirSeries(deviceId, range = '7d') {
  const rand = seededRandom(hashString(deviceId) ^ hashString('pir' + range));
  const stamps = timeAxis(range);
  return stamps.map((SK) => {
    const triggered = rand() < 0.18;
    return {
      PK: deviceId,
      SK,
      sensor_type: 'pir',
      payload: triggered
        ? { pir: 'trigger', duration: Math.round(5 + rand() * 115) }
        : { pir: 'normal' },
    };
  });
}

// --- Line rating (#247) -----------------------------------------------------
//
// What the line rating panel shows to every tier below superuser. Generated
// once from the real model (DataIngestionESM/lib/dlrSettings.mjs buildRating)
// with example settings: a 243-AL1/39-ST1A conductor at 80 °C, a placeholder
// static rating of 645 A, and a calm autumn reading of 14.6 °C and 1.2 m/s. It
// is a picture of the panel, not of any real line, and the panel says so.
const FAKE_DLR_SNAPSHOT = {
  "settings": {
    "settings": {
      "emissivity": 0.5,
      "absorptivity": 0.5,
      "station_height_m": 5,
      "conductor_height_m": 27.5,
      "method": "ieee738",
      "height_correction": "off",
      "static_reference_wind_ms": 0.6,
      "t_max_c": 80,
      "static_rating_a": 645,
      "line_azimuth_deg": 45,
      "elevation_m": 50,
      "conductor_id": "acsr-243-39"
    },
    "version": 1,
    "updated_at": null,
    "updated_by": null
  },
  "options": {
    "conductors": [
      {
        "id": "acsr-149-24",
        "label": "149-AL1/24-ST1A (150/25)",
        "standard": "EN 50182",
        "diameter_mm": 17.1,
        "strand_diameter_mm": 2.7,
        "r_low_ohm_per_km": 0.19778,
        "t_low_c": 20,
        "r_high_ohm_per_km": 0.2456,
        "t_high_c": 80
      },
      {
        "id": "acsr-243-39",
        "label": "243-AL1/39-ST1A (240/40)",
        "standard": "EN 50182",
        "diameter_mm": 21.8,
        "strand_diameter_mm": 3.45,
        "r_low_ohm_per_km": 0.12118,
        "t_low_c": 20,
        "r_high_ohm_per_km": 0.15048,
        "t_high_c": 80
      },
      {
        "id": "acsr-382-49",
        "label": "382-AL1/49-ST1A (380/50)",
        "standard": "EN 50182",
        "diameter_mm": 27,
        "strand_diameter_mm": 3,
        "r_low_ohm_per_km": 0.07721,
        "t_low_c": 20,
        "r_high_ohm_per_km": 0.09588,
        "t_high_c": 80
      },
      {
        "id": "acsr-490-64",
        "label": "490-AL1/64-ST1A (490/65)",
        "standard": "EN 50182",
        "diameter_mm": 30.6,
        "strand_diameter_mm": 3.4,
        "r_low_ohm_per_km": 0.06018,
        "t_low_c": 20,
        "r_high_ohm_per_km": 0.07473,
        "t_high_c": 80
      },
      {
        "id": "acsr-drake",
        "label": "ACSR Drake 795 kcmil 26/7 (IEEE 738 / TB 601 examples)",
        "standard": "IEEE 738",
        "diameter_mm": 28.1,
        "strand_diameter_mm": 4.4,
        "r_low_ohm_per_km": 0.07283,
        "t_low_c": 25,
        "r_high_ohm_per_km": 0.08688,
        "t_high_c": 75
      }
    ],
    "methods": [
      "ieee738",
      "cigre601"
    ],
    "height_corrections": [
      "off",
      "open",
      "scattered",
      "rough"
    ],
    "defaults": {
      "emissivity": 0.5,
      "absorptivity": 0.5,
      "station_height_m": 5,
      "conductor_height_m": 27.5,
      "method": "ieee738",
      "height_correction": "off"
    },
    "max_reading_age_min": 30,
    "low_wind_ms": 0.5
  },
  "status": "ok",
  "weather": {
    "measured_at": "2026-11-01T10:56:00Z",
    "age_min": 4,
    "air_temperature_c": 14.6,
    "wind_speed_ms": 1.2,
    "wind_direction_deg": 330,
    "attack_deg": 75,
    "attack_source": "measured",
    "low_wind_ms": 0.5
  },
  "rating": {
    "method": "ieee738",
    "t_max_c": 80,
    "ampacity_a": 876,
    "static_rating_a": 645,
    "ratio_to_static": 1.36,
    "aar_a": 765,
    "aar_reference_wind_ms": 0.6,
    "heat_balance": {
      "q_convection_w_per_m": 105.4,
      "q_radiation_w_per_m": 16.9,
      "q_solar_w_per_m": 6.7,
      "r_ohm_per_km_at_t_max": 0.15048
    },
    "temperature_curve": [
      {
        "t_c": 16,
        "current_a": 0
      },
      {
        "t_c": 18,
        "current_a": 0
      },
      {
        "t_c": 20,
        "current_a": 159.3
      },
      {
        "t_c": 22,
        "current_a": 234.3
      },
      {
        "t_c": 24,
        "current_a": 289.9
      },
      {
        "t_c": 26,
        "current_a": 335.8
      },
      {
        "t_c": 28,
        "current_a": 375.6
      },
      {
        "t_c": 30,
        "current_a": 411.1
      },
      {
        "t_c": 32,
        "current_a": 443.4
      },
      {
        "t_c": 34,
        "current_a": 473.1
      },
      {
        "t_c": 36,
        "current_a": 500.6
      },
      {
        "t_c": 38,
        "current_a": 526.5
      },
      {
        "t_c": 40,
        "current_a": 550.8
      },
      {
        "t_c": 42,
        "current_a": 573.7
      },
      {
        "t_c": 44,
        "current_a": 595.6
      },
      {
        "t_c": 46,
        "current_a": 616.4
      },
      {
        "t_c": 48,
        "current_a": 636.3
      },
      {
        "t_c": 50,
        "current_a": 655.4
      },
      {
        "t_c": 52,
        "current_a": 673.7
      },
      {
        "t_c": 54,
        "current_a": 691.3
      },
      {
        "t_c": 56,
        "current_a": 708.3
      },
      {
        "t_c": 58,
        "current_a": 724.7
      },
      {
        "t_c": 60,
        "current_a": 740.6
      },
      {
        "t_c": 62,
        "current_a": 755.9
      },
      {
        "t_c": 64,
        "current_a": 770.8
      },
      {
        "t_c": 66,
        "current_a": 785.3
      },
      {
        "t_c": 68,
        "current_a": 799.4
      },
      {
        "t_c": 70,
        "current_a": 813.1
      },
      {
        "t_c": 72,
        "current_a": 826.4
      },
      {
        "t_c": 74,
        "current_a": 839.3
      },
      {
        "t_c": 76,
        "current_a": 852
      },
      {
        "t_c": 78,
        "current_a": 864.3
      },
      {
        "t_c": 80,
        "current_a": 876.4
      },
      {
        "t_c": 82,
        "current_a": 888.2
      },
      {
        "t_c": 84,
        "current_a": 899.7
      },
      {
        "t_c": 86,
        "current_a": 911
      },
      {
        "t_c": 88,
        "current_a": 922
      },
      {
        "t_c": 90,
        "current_a": 932.8
      },
      {
        "t_c": 92,
        "current_a": 943.4
      },
      {
        "t_c": 94,
        "current_a": 953.8
      },
      {
        "t_c": 96,
        "current_a": 964
      },
      {
        "t_c": 98,
        "current_a": 974
      },
      {
        "t_c": 100,
        "current_a": 983.8
      },
      {
        "t_c": 102,
        "current_a": 993.4
      },
      {
        "t_c": 104,
        "current_a": 1002.9
      },
      {
        "t_c": 106,
        "current_a": 1012.2
      },
      {
        "t_c": 108,
        "current_a": 1021.3
      },
      {
        "t_c": 110,
        "current_a": 1030.3
      }
    ]
  },
  "height_corrected": null
};

export function generateFakeDlr(deviceId) {
  const measuredAt = new Date(Date.now() - 4 * 60000).toISOString();
  return {
    ...FAKE_DLR_SNAPSHOT,
    device_id: deviceId,
    computed_at: new Date().toISOString(),
    weather: { ...FAKE_DLR_SNAPSHOT.weather, measured_at: measuredAt, age_min: 4 },
  };
}
